import React, { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { addDays, addMonths, addYears } from 'date-fns'
import { useTranslation } from 'react-i18next'

import {
  FormControl,
  TextInput,
  Dropdown,
  DatePicker,
  InputGroup,
  Radio,
  Loader,
} from 'components'
import { validateKeyPress, trimFirstSpace } from 'helpers/common'
import { DATE_UNIT, ProductForForm, WARRANTY_TYPE } from 'types'

const WarrantySection: React.FC = () => {
  const [warrantyPeriodChecking, setWarrantyPeriodChecking] =
    useState<boolean>(false)
  const [maintenancePeriodChecking, setMaintenancePeriodChecking] =
    useState<boolean>(false)
  const { control, errors, watch, setValue, trigger, clearErrors } =
    useFormContext<ProductForForm>()
  const { startWarranty, warrantyUnit, warrantyPeriod } = watch([
    'startWarranty',
    'warrantyUnit',
    'warrantyPeriod',
    'qrCode',
  ])
  const warrantyType = watch('warrantyType')
  const maintenancePeriod = watch('maintenancePeriod')
  const maintenanceUnit = watch('maintenanceUnit')
  const { productId } = useParams<{ id: string; productId?: string }>()
  const { t } = useTranslation('product')

  const isEdit = Boolean(productId)
  const base = 'f-product-form'
  const isPeriod = warrantyType === WARRANTY_TYPE.PERIOD
  const isLifetime = warrantyType === WARRANTY_TYPE.LIFETIME
  const periodOption = Object.values(DATE_UNIT).map((value) => {
    let labelName = t('warranty.unit.day')

    if (value.toLowerCase() === 'month') {
      labelName = t('warranty.unit.month')
    }

    if (value.toLowerCase() === 'year') {
      labelName = t('warranty.unit.year')
    }

    return {
      key: value,
      label: labelName,
    }
  })

  const addDateType = (type: DATE_UNIT) => {
    switch (type) {
      case DATE_UNIT.YEAR:
        return addYears
      case DATE_UNIT.MONTH:
        return addMonths
      default:
        return addDays
    }
  }

  useEffect(() => {
    if (!startWarranty || !warrantyUnit || !warrantyPeriod) {
      return setValue('endWarranty', null, { shouldDirty: true })
    }

    const periodType = warrantyUnit
    clearErrors('endWarranty')

    return setValue(
      'endWarranty',
      addDateType(periodType)(startWarranty, Number(warrantyPeriod)),
    )
  }, [startWarranty, warrantyUnit, warrantyPeriod])

  useEffect(() => {
    if (warrantyPeriod && warrantyUnit) {
      clearErrors(['warrantyPeriod', 'warrantyUnit'])
    }
  }, [warrantyPeriod, warrantyUnit])

  useEffect(() => {
    if (maintenancePeriod && maintenanceUnit) {
      clearErrors(['maintenancePeriod', 'maintenanceUnit'])
    }
  }, [maintenancePeriod, maintenanceUnit])

  return (
    <div className={`${base}__section--with-border`}>
      <div className={`${base}__title-layout`}>
        <span className={`${base}__title`}>{t('warranty.title')}</span>
      </div>
      <div className={`${base}__description-layout-warranty `}>
        <Controller
          name='warrantyType'
          control={control}
          render={(fields) => (
            <div className={`${base}__warranty-type-con`}>
              <Radio
                placeholder={t('warranty.period.label')}
                checked={fields.value === WARRANTY_TYPE.PERIOD}
                onChange={() => fields.onChange(WARRANTY_TYPE.PERIOD)}
                name={fields.name}
                id={WARRANTY_TYPE.PERIOD}
                value={WARRANTY_TYPE.PERIOD}
                data-testid='radio-period-product'
              />
              <Radio
                placeholder={t('warranty.lifetime.label')}
                checked={fields.value === WARRANTY_TYPE.LIFETIME}
                onChange={() => fields.onChange(WARRANTY_TYPE.LIFETIME)}
                name={fields.name}
                id={WARRANTY_TYPE.LIFETIME}
                value={WARRANTY_TYPE.LIFETIME}
                data-testid='radio-lifetime-product'
              />
              <Radio
                placeholder={t('warranty.none.label')}
                checked={fields.value === WARRANTY_TYPE.NONE}
                onChange={() => fields.onChange(WARRANTY_TYPE.NONE)}
                name={fields.name}
                id={WARRANTY_TYPE.NONE}
                value={WARRANTY_TYPE.NONE}
                data-testid='radio-none-product'
              />
            </div>
          )}
        />
        <div className={`${base}__grid`}>
          {isPeriod && (
            <FormControl label={t('warranty.time.label')} required>
              <InputGroup
                className={`${base}__half-con`}
                error={
                  errors.warrantyUnit?.message || errors.warrantyPeriod?.message
                }
              >
                <Controller
                  name='warrantyPeriod'
                  control={control}
                  render={(field) => (
                    <TextInput
                      className={`${base}__half-item`}
                      type='string'
                      group='right'
                      placeholder={t('warranty.period.placeholder')}
                      isError={
                        Boolean(errors.warrantyUnit?.message) ||
                        Boolean(errors.warrantyPeriod?.message)
                      }
                      maxLength={2}
                      value={field.value}
                      onChange={field.onChange}
                      onKeyPress={validateKeyPress(new RegExp(/[\p{N}]/u))}
                      onBlur={async () => {
                        if (!isEdit || !field.value) {
                          setWarrantyPeriodChecking(true)
                          await trigger('warrantyPeriod')
                          setWarrantyPeriodChecking(false)
                        }
                      }}
                      withIcon={warrantyPeriodChecking && <Loader />}
                      ref={field.ref}
                    />
                  )}
                />
                <Controller
                  name='warrantyUnit'
                  control={control}
                  render={({ value }) => (
                    <Dropdown
                      group='left'
                      placeholder={t('warranty.unit.placeholder')}
                      options={periodOption}
                      selected={periodOption.find(
                        (element) => element.key === value,
                      )}
                      onChange={(selected) =>
                        setValue('warrantyUnit', selected.key, {
                          shouldDirty: true,
                        })
                      }
                      onBlur={async () => {
                        if (!isEdit) {
                          setWarrantyPeriodChecking(true)
                          await trigger('warrantyPeriod')
                          setWarrantyPeriodChecking(false)
                        }
                      }}
                      isError={
                        Boolean(errors.warrantyPeriod?.message) ||
                        Boolean(errors.warrantyUnit?.message)
                      }
                    />
                  )}
                />
              </InputGroup>
            </FormControl>
          )}
          {(isPeriod || isLifetime) && (
            <FormControl label={t('warranty.maintenancePeriod.label')} required>
              <InputGroup
                className={`${base}__half-con`}
                error={
                  errors.maintenanceUnit?.message ||
                  errors.maintenancePeriod?.message
                }
              >
                <Controller
                  name='maintenancePeriod'
                  control={control}
                  render={(field) => (
                    <TextInput
                      value={field.value}
                      className={`${base}__half-item`}
                      group='right'
                      placeholder={t('warranty.maintenancePeriod.placeholder')}
                      isError={
                        Boolean(errors.maintenanceUnit?.message) ||
                        Boolean(errors.maintenancePeriod?.message)
                      }
                      maxLength={2}
                      onChange={field.onChange}
                      onKeyPress={validateKeyPress(new RegExp(/[\p{N}]/u))}
                      onBlur={async () => {
                        if (!isEdit || !field.value) {
                          setMaintenancePeriodChecking(true)
                          await trigger('maintenancePeriod')
                          setMaintenancePeriodChecking(false)
                        }
                      }}
                      withIcon={maintenancePeriodChecking && <Loader />}
                      ref={field.ref}
                    />
                  )}
                />
                <Controller
                  name='maintenanceUnit'
                  control={control}
                  render={({ value }) => (
                    <Dropdown
                      placeholder={t('warranty.maintenanceUnit.placeholder')}
                      group='left'
                      options={periodOption}
                      selected={periodOption.find(
                        (element) => element.key === value,
                      )}
                      onChange={(selected) =>
                        setValue('maintenanceUnit', selected.key, {
                          shouldDirty: true,
                        })
                      }
                      onBlur={async () => {
                        if (!isEdit) {
                          setMaintenancePeriodChecking(true)
                          await trigger('maintenancePeriod')
                          setMaintenancePeriodChecking(false)
                        }
                      }}
                      isError={
                        Boolean(errors.maintenancePeriod?.message) ||
                        Boolean(errors.maintenanceUnit?.message)
                      }
                    />
                  )}
                />
              </InputGroup>
            </FormControl>
          )}
        </div>
        {isPeriod && (
          <div className={`${base}__period-con`}>
            <div className={`${base}__grid`}>
              <FormControl label={t('warranty.startDate.label')} required>
                <Controller
                  name='startWarranty'
                  control={control}
                  render={(fields) => {
                    return (
                      <DatePicker
                        placeholder={t('warranty.startDate.placeholder')}
                        value={fields.value}
                        onChange={(date) => {
                          fields.onChange(date)
                          clearErrors('startWarranty')
                        }}
                        isError={Boolean(errors.startWarranty?.message)}
                        message={errors.startWarranty?.message}
                      />
                    )
                  }}
                />
              </FormControl>
              <FormControl label={t('warranty.endDate.label')}>
                <Controller
                  name='endWarranty'
                  control={control}
                  render={(fields) => (
                    <DatePicker
                      {...fields}
                      placeholder={t('warranty.endDate.placeholder')}
                      disabled
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>
        )}

        <FormControl label={t('warranty.qrcode.label')}>
          <div className={`${base}__qr-code-con`}>
            <Controller
              name='qrCode'
              control={control}
              render={(field) => {
                const isError = Boolean(errors.qrCode?.message)

                return (
                  <TextInput
                    isWarranty
                    type='text'
                    placeholder={t('warranty.qrcode.placeholder')}
                    isError={isError}
                    message={errors.qrCode?.message}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(trimFirstSpace(e))
                      clearErrors('qrCode')
                    }}
                    onKeyPress={validateKeyPress(new RegExp(/^[a-zA-Z0-9]$/))}
                    maxLength={15}
                    ref={field.ref}
                  />
                )
              }}
            />
          </div>
        </FormControl>
      </div>
    </div>
  )
}

export default WarrantySection
